import { Fragment, type ReactNode } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { CountChip } from "@/components/count-chip";
import { CountryTag } from "@/components/country-tag";
import { CountryChips } from "@/components/table-chips";
import { Icon } from "@/components/icons";
import { SelectCheckbox } from "@/components/select-checkbox";
import { Tooltip } from "@/components/tooltip";
import { formatDate } from "@/app/(app)/_lib/format";
import {
  ColumnResizeHandle,
  buildResizableGridTemplate,
  resizeHandlerFor,
} from "@/components/column-resize";
import type { MonitoringEntry } from "../_data/types";
import {
  COLUMN_IDS,
  ENTRIES_LOCKED_FIRST,
  type ColumnId,
  type EntriesSort,
  type EntriesSortKey,
} from "./entries-table-utils";

type Column<Row> = {
  id: ColumnId;
  header: string;
  /** Grid-template-columns fragment, e.g. "minmax(160px,1.4fr)" or "88px". */
  width: string;
  align?: "left" | "right" | "center";
  /** Optional info tooltip rendered next to the header label. */
  info?: { title: string; description: string };
  cell: (row: Row) => ReactNode;
};

const SELECT_COL_WIDTH = "40px";

const SORTABLE = new Set<ColumnId>(COLUMN_IDS);

const COLUMNS_BY_ID: Record<ColumnId, Column<MonitoringEntry>> = {
  name: {
    id: "name",
    header: "Name",
    // Name is the primary column — give it the largest min + fr weight so it
    // claims most of the spare width and entity names stop truncating (#64).
    width: "minmax(200px,2.6fr)",
    cell: (e) => <div className="truncate px-2">{e.name || "—"}</div>,
  },
  identifier: {
    id: "identifier",
    header: "Identifier",
    width: "minmax(120px,1fr)",
    cell: (e) => <div className="truncate px-2 text-grey-900">{e.identifier || "—"}</div>,
  },
  externalId: {
    id: "externalId",
    header: "External ID",
    width: "minmax(120px,1fr)",
    cell: (e) => <div className="truncate px-2 text-grey-900">{e.externalId || "—"}</div>,
  },
  type: {
    id: "type",
    header: "Type",
    width: "minmax(100px,0.7fr)",
    cell: (e) => <div className="truncate px-2 text-grey-900">{e.type || "—"}</div>,
  },
  country: {
    id: "country",
    header: "Country",
    width: "minmax(120px,0.8fr)",
    // 1-2 countries: render each as its own flag+ISO2 chip (mirrors
    // results-table.tsx's Country column). More than 2 stops fitting the
    // column cleanly, so it collapses to a count chip (CountryChips) instead.
    cell: (e) => {
      const countries = e.countries ?? (e.country ? [e.country] : []);
      if (countries.length === 0) {
        return <div className="truncate px-2 text-grey-900">—</div>;
      }
      if (countries.length > 2) {
        return (
          <div className="truncate px-2">
            <CountryChips countries={countries} />
          </div>
        );
      }
      return (
        <div className="flex min-w-0 items-center gap-1.5 overflow-hidden px-2">
          {countries.map((iso) => (
            <CountryTag key={iso} iso={iso} />
          ))}
        </div>
      );
    },
  },
  dob: {
    id: "dob",
    header: "DOB",
    width: "minmax(100px,0.7fr)",
    cell: (e) => <div className="truncate px-2 text-black">{e.dob ? formatDate(e.dob) : "—"}</div>,
  },
  dateAdded: {
    id: "dateAdded",
    header: "Date added",
    width: "minmax(120px,0.9fr)",
    cell: (e) => <div className="px-2 text-black">{formatDate(e.dateAdded)}</div>,
  },
  matches: {
    id: "matches",
    header: "Matches",
    width: "100px",
    cell: (e) =>
      e.status === "PROCESSING" ? (
        <ProcessingCell />
      ) : (
        <div className="px-2 text-grey-900">{e.matches}</div>
      ),
  },
  fp: {
    id: "fp",
    header: "FP",
    width: "88px",
    info: {
      title: "False matches",
      description: "Match reviewed and dismissed.",
    },
    cell: (e) => <div className="px-2 text-grey-900">{e.fp}</div>,
  },
  rp: {
    id: "rp",
    header: "RP",
    width: "88px",
    info: {
      title: "Real matches",
      description: "Match confirmed as relevant.",
    },
    cell: (e) => <div className="px-2 text-grey-900">{e.rp}</div>,
  },
  newCount: {
    id: "newCount",
    header: "New",
    width: "88px",
    cell: (e) => (
      <div className="flex px-2">
        <CountChip count={e.newCount} />
      </div>
    ),
  },
};

const LOCKED_FIRST_SET = new Set<string>(ENTRIES_LOCKED_FIRST);

function visibleColumns(
  order: readonly ColumnId[],
  hidden: ReadonlySet<ColumnId>,
): Column<MonitoringEntry>[] {
  return order.filter((id) => !hidden.has(id)).map((id) => COLUMNS_BY_ID[id]);
}

function buildGridTemplate(
  columns: Column<MonitoringEntry>[],
  selectMode: boolean,
  widths: Partial<Record<ColumnId, number>> = {},
): string {
  const columnsTemplate = buildResizableGridTemplate(columns, widths);
  return selectMode ? `${SELECT_COL_WIDTH} ${columnsTemplate}` : columnsTemplate;
}

// ===========================================================================
// Header — draggable, sortable
// ===========================================================================

type EntriesTableHeaderProps = {
  order: readonly ColumnId[];
  hidden: ReadonlySet<ColumnId>;
  onReorder: (next: ColumnId[]) => void;
  selectMode?: boolean;
  allSelected?: boolean;
  someSelected?: boolean;
  onToggleAll?: () => void;
  sort?: EntriesSort;
  onSort?: (key: EntriesSortKey) => void;
  widths?: Partial<Record<ColumnId, number>>;
  onResizeColumn?: (id: ColumnId, px: number) => void;
};

export function EntriesTableHeader({
  order,
  hidden,
  onReorder,
  selectMode = false,
  allSelected = false,
  someSelected = false,
  onToggleAll,
  sort,
  onSort,
  widths,
  onResizeColumn,
}: EntriesTableHeaderProps) {
  const columns = visibleColumns(order, hidden);
  const visibleIds = columns.map((c) => c.id);
  const draggableIds = visibleIds.filter((id) => !LOCKED_FIRST_SET.has(id));
  const gridTemplate = buildGridTemplate(columns, selectMode, widths);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    if (LOCKED_FIRST_SET.has(active.id as string)) return;
    const oldIndex = order.indexOf(active.id as ColumnId);
    const rawNewIndex = order.indexOf(over.id as ColumnId);
    if (oldIndex < 0 || rawNewIndex < 0) return;
    const newIndex = Math.max(ENTRIES_LOCKED_FIRST.length, rawNewIndex);
    if (oldIndex === newIndex) return;
    onReorder(arrayMove(order as ColumnId[], oldIndex, newIndex));
  }

  const headerGrid = (
    <div
      className="grid h-9 items-center border-b border-grey-200 bg-grey-50 text-small font-medium text-grey-900"
      style={{ gridTemplateColumns: gridTemplate }}
    >
      {selectMode && (
        <div className="flex items-center justify-center">
          <SelectCheckbox
            checked={allSelected}
            indeterminate={!allSelected && someSelected}
            onClick={onToggleAll}
            label={allSelected ? "Deselect all" : "Select all"}
          />
        </div>
      )}
      {columns.map((column, index) => {
        const resizable = resizeHandlerFor(index, columns.length, onResizeColumn);
        return !LOCKED_FIRST_SET.has(column.id) ? (
          <SortableHeaderCell
            key={column.id}
            column={column}
            sort={sort}
            onSort={onSort}
            onResizeColumn={resizable}
          />
        ) : (
          <StaticHeaderCell
            key={column.id}
            column={column}
            locked={LOCKED_FIRST_SET.has(column.id)}
            sort={sort}
            onSort={onSort}
            onResizeColumn={resizable}
          />
        );
      })}
    </div>
  );

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={draggableIds} strategy={horizontalListSortingStrategy}>
        {headerGrid}
      </SortableContext>
    </DndContext>
  );
}

function StaticHeaderCell({
  column,
  locked = false,
  sort,
  onSort,
  onResizeColumn,
}: {
  column: Column<MonitoringEntry>;
  /** Locked-first columns omit the drag-handle spacer entirely. */
  locked?: boolean;
  sort?: EntriesSort;
  onSort?: (key: EntriesSortKey) => void;
  onResizeColumn?: (id: ColumnId, px: number) => void;
}) {
  return (
    <div
      role="columnheader"
      data-column-id={column.id}
      className={[
        "relative flex h-full items-center gap-2 select-none",
        headerJustify(column.align),
      ].join(" ")}
    >
      {!locked && <span className="h-5 w-2 shrink-0" aria-hidden />}
      <HeaderCellContent column={column} sort={sort} onSort={onSort} />
      {onResizeColumn && <ColumnResizeHandle columnId={column.id} onResize={onResizeColumn} />}
    </div>
  );
}

function SortableHeaderCell({
  column,
  sort,
  onSort,
  onResizeColumn,
}: {
  column: Column<MonitoringEntry>;
  sort?: EntriesSort;
  onSort?: (key: EntriesSortKey) => void;
  onResizeColumn?: (id: ColumnId, px: number) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.id,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      role="columnheader"
      data-column-id={column.id}
      className={[
        "relative flex h-full items-center gap-2 select-none",
        headerJustify(column.align),
      ].join(" ")}
    >
      <DragHandle attributes={attributes} listeners={listeners} />
      <HeaderCellContent column={column} sort={sort} onSort={onSort} />
      {onResizeColumn && <ColumnResizeHandle columnId={column.id} onResize={onResizeColumn} />}
    </div>
  );
}

function headerJustify(align: Column<MonitoringEntry>["align"]) {
  if (align === "right") return "justify-end";
  if (align === "center") return "justify-center";
  return "justify-start";
}

function DragHandle({
  attributes,
  listeners,
}: {
  attributes: ReturnType<typeof useSortable>["attributes"];
  listeners: ReturnType<typeof useSortable>["listeners"];
}) {
  return (
    <button
      type="button"
      aria-label="Drag column to reorder"
      {...attributes}
      {...listeners}
      className="flex h-5 w-2 shrink-0 cursor-grab items-center justify-center text-grey-300 hover:text-grey-900 active:cursor-grabbing touch-none"
    >
      <DragHandleIcon />
    </button>
  );
}

function DragHandleIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="8"
      height="20"
      viewBox="0 0 8 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <circle cx="2" cy="6" r="1.25" />
      <circle cx="6" cy="6" r="1.25" />
      <circle cx="2" cy="10" r="1.25" />
      <circle cx="6" cy="10" r="1.25" />
      <circle cx="2" cy="14" r="1.25" />
      <circle cx="6" cy="14" r="1.25" />
    </svg>
  );
}

function HeaderCellContent({
  column,
  sort,
  onSort,
}: {
  column: Column<MonitoringEntry>;
  sort?: EntriesSort;
  onSort?: (key: EntriesSortKey) => void;
}) {
  const sortable = SORTABLE.has(column.id) && !!onSort;
  const activeDir = sortable && sort && sort.key === column.id ? sort.dir : null;
  const glyph =
    activeDir === "asc" ? "arrow_upward" : activeDir === "desc" ? "arrow_downward" : "unfold_more";
  const sortAriaLabel = activeDir
    ? `${column.header}, sorted ${activeDir === "asc" ? "ascending" : "descending"}`
    : `Sort by ${column.header}`;

  if (column.info) {
    // gap-2 = Figma's 8px label↔info-icon spacing (frame 15649-42882).
    return (
      <div className={`flex items-center gap-2 px-2 ${headerJustify(column.align)}`}>
        {sortable ? (
          <button
            type="button"
            onClick={() => onSort?.(column.id as EntriesSortKey)}
            aria-label={sortAriaLabel}
            className="flex items-center gap-1 transition-colors hover:text-black"
          >
            <span className="whitespace-nowrap">{column.header}</span>
            <Icon
              name={glyph}
              size={20}
              weight={300}
              className={activeDir ? "text-grey-900" : "text-grey-500"}
            />
          </button>
        ) : (
          <span className="whitespace-nowrap">{column.header}</span>
        )}
        <Tooltip title={column.info.title} description={column.info.description} align="end">
          <span className="flex size-5 items-center justify-center text-grey-500 transition-colors hover:text-grey-900">
            <Icon name="info" size={20} filled label="More info" />
          </span>
        </Tooltip>
      </div>
    );
  }

  if (sortable) {
    return (
      <button
        type="button"
        onClick={() => onSort?.(column.id as EntriesSortKey)}
        aria-label={sortAriaLabel}
        className={`flex items-center gap-1 px-2 transition-colors hover:text-black ${headerJustify(column.align)}`}
      >
        <span className="whitespace-nowrap">{column.header}</span>
        <Icon
          name={glyph}
          size={20}
          weight={300}
          className={activeDir ? "text-grey-900" : "text-grey-500"}
        />
      </button>
    );
  }

  const textAlign =
    column.align === "right"
      ? "text-right"
      : column.align === "center"
        ? "text-center"
        : "text-left";
  return <div className={`whitespace-nowrap px-2 ${textAlign}`}>{column.header}</div>;
}

// ===========================================================================
// Body
// ===========================================================================

type EntriesTableProps = {
  entries: MonitoringEntry[];
  order: readonly ColumnId[];
  hidden: ReadonlySet<ColumnId>;
  onSelect?: (id: string) => void;
  selectMode?: boolean;
  selectedIds?: ReadonlySet<string>;
  onToggleSelect?: (id: string) => void;
  widths?: Partial<Record<ColumnId, number>>;
};

export function EntriesTable({
  entries,
  order,
  hidden,
  onSelect,
  selectMode = false,
  selectedIds,
  onToggleSelect,
  widths,
}: EntriesTableProps) {
  const columns = visibleColumns(order, hidden);
  const gridTemplateColumns = buildGridTemplate(columns, selectMode, widths);

  return (
    <div className="flex flex-col bg-white">
      {entries.map((entry) => {
        const isProcessing = entry.status === "PROCESSING";
        const isSelected = selectedIds?.has(entry.id) ?? false;
        return (
          <button
            key={entry.id}
            type="button"
            onClick={() => {
              if (isProcessing) return;
              if (selectMode) {
                onToggleSelect?.(entry.id);
              } else {
                onSelect?.(entry.id);
              }
            }}
            style={{ gridTemplateColumns }}
            className={[
              "grid h-12 items-center border-b border-grey-200 text-left text-small text-black",
              isProcessing
                ? "cursor-default"
                : isSelected
                  ? "cursor-pointer bg-green-500-12 hover:bg-green-500-12"
                  : "cursor-pointer hover:bg-grey-50",
            ].join(" ")}
          >
            {selectMode && (
              <div className="flex items-center justify-center">
                {isProcessing ? (
                  <span className="size-5" aria-hidden />
                ) : (
                  <SelectCheckbox
                    checked={isSelected}
                    label={isSelected ? "Deselect row" : "Select row"}
                  />
                )}
              </div>
            )}
            {columns.map((column) => (
              <Fragment key={column.id}>{column.cell(entry)}</Fragment>
            ))}
          </button>
        );
      })}
    </div>
  );
}

// ===========================================================================
// Helpers
// ===========================================================================

function ProcessingCell() {
  return (
    <div className="px-2 text-small italic text-grey-400" aria-label="Processing">
      Processing…
    </div>
  );
}
